import React, { useContext, useEffect, useState } from "react";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import BondMonitor from "../BondMonitor";
import ArbMonitor from "../ArbMonitor";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import { getRiskTableColumns } from "../table/columnsConfig/riskTableColumns";
import { formatCurrency } from "../../utils/dataCalculations";
import { CardWrapper, PageHeader, TabNavigation } from "../ui";
import { useNav } from "../../context/NavContext";
import MABSummaryStats from "../MABSummaryStats";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";

const MAB_Risk = ({ dashboardOnly = false }) => {
  const NAV_NUMBER = useNav();

  // ===== CONFIG =====
  const tabs = [
    { value: "tab1", label: "MAB Dashboard" },
    { value: "tab2", label: "Portfolio Risk" },
    { value: "tab3", label: "Arb Monitor" },
    { value: "tab4", label: "Bond Monitor" },
    { value: "tab5", label: "Missing Data" },
  ];

  // ===== UTILITY FUNCTIONS =====
  const formatAmount = (amount) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}mm`;
    } else if (absAmount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k`;
    }
    return amount.toFixed(1);
  };

  const getNextMonthsPnL = (positions) => {
    const today = new Date();
    const nextMonths = Array.from({ length: 3 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      return {
        month: date.getMonth(),
        year: date.getFullYear(),
        monthYear: date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        grossPnL: 0,
        netPnL: 0,
        deals: 0,
      };
    });

    positions.forEach((position) => {
      if (!position.closing_date) return;

      const closeDate = new Date(position.closing_date);
      const monthIndex = nextMonths?.findIndex(
        (m) =>
          m.month === closeDate.getMonth() && m.year === closeDate.getFullYear()
      );

      if (monthIndex !== -1) {
        nextMonths[monthIndex].grossPnL +=
          Number(position.gross_pnl_to_dealclose) || 0;
        nextMonths[monthIndex].netPnL +=
          Number(position.net_pnl_to_dealclose) || 0;
        nextMonths[monthIndex].deals += 1;
      }
    });

    return nextMonths;
  };

  const getTopStrategiesByPnL = (positions) => {
    const strategyStats = positions.reduce((acc, position) => {
      const strategy = position.StrategyName || "Unknown";
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          grossPnL: 0,
          netPnL: 0,
          risk: 0,
        };
      }
      acc[strategy].grossPnL += Number(position.gross_pnl_to_dealclose) || 0;
      acc[strategy].netPnL += Number(position.net_pnl_to_dealclose) || 0;
      acc[strategy].risk += Number(position.risk_to_downside) || 0;
      return acc;
    }, {});

    return Object.values(strategyStats)
      .sort((a, b) => b.grossPnL - a.grossPnL)
      .slice(0, 3);
  };

  const getStrategyPnL = (positions) => {
    const strategyStats = positions.reduce((acc, position) => {
      const strategy = position.StrategyName || "Unknown";
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          pnl: 0,
          numPositions: 0,
        };
      }
      acc[strategy].pnl += Number(position.total_pnl) || 0;
      acc[strategy].numPositions += 1;
      return acc;
    }, {});

    return Object.values(strategyStats);
  };

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    // Convert both to strings to handle numbers
    const textStr = String(text);
    const searchStr = String(searchTerm);

    // Case insensitive search
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);

    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: "rgb(250, 211, 104)" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // ===== STATE =====
  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Initialize activeTab with the saved value from localStorage
  const [activeTab, setActiveTab] = useState(() => {
    if (dashboardOnly) return "tab1";
    const savedTab = localStorage.getItem("mabRiskActiveTab");
    return savedTab || "tab1";
  });

  // Initialize expanded rows states
  const [expandedRiskRows, setExpandedRiskRows] = useState(() => {
    const saved = localStorage.getItem("mabRiskTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedMissingPricesRows, setExpandedMissingPricesRows] = useState(
    () => {
      const saved = localStorage.getItem(
        "mabRiskMissingPricesTableExpandedRows"
      );
      return saved ? JSON.parse(saved) : {};
    }
  );

  // Data states
  const [allData, setAllData] = useState([]);
  const [MABData, setMABData] = useState([]);
  const [
    positions_with_downside_prices_data,
    setPositions_with_downside_prices_data,
  ] = useState([]);
  const [
    positions_with_missing_downside_prices,
    setPositions_with_missing_downside_prices,
  ] = useState([]);
  const [arb_monitor_data, setArbMonitorData] = useState([]);
  const [bond_monitor_data, setBond_monitor_data] = useState([]);

  // Initialize filter states
  const [savedGlobalFilterRisk, setSavedGlobalFilterRisk] = useState(() => {
    return localStorage.getItem("mabRiskTableGlobalFilter") || "";
  });
  const [globalFilterRisk, setGlobalFilterRisk] = useState(
    savedGlobalFilterRisk
  );

  const [savedGlobalFilterMissingPrices, setSavedGlobalFilterMissingPrices] =
    useState(() => {
      return (
        localStorage.getItem("mabRiskMissingPricesTableGlobalFilter") || ""
      );
    });
  const [globalFilterMissingPrices, setGlobalFilterMissingPrices] = useState(
    savedGlobalFilterMissingPrices
  );

  const [savedGlobalFilterArbMonitor, setSavedGlobalFilterArbMonitor] =
    useState(() => {
      return localStorage.getItem("mabRiskArbMonitorTableGlobalFilter") || "";
    });
  const [globalFilterArbMonitor, setGlobalFilterArbMonitor] = useState(
    savedGlobalFilterArbMonitor
  );

  // ===== EFFECTS =====
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
    if (datasets?.arbMonitorData) setArbMonitorData(datasets.arbMonitorData);
    if (datasets?.bondMonitorData)
      setBond_monitor_data(datasets.bondMonitorData);
  }, [datasets]);

  // Save scroll position to localStorage on scroll
  useEffect(() => {
    const handleScroll = () => {
      localStorage.setItem("mabRiskScrollPosition", window.pageYOffset);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Restore scroll position on mount (and after refresh)
  useEffect(() => {
    const storedScrollPosition =
      parseInt(localStorage.getItem("mabRiskScrollPosition"), 10) || 0;
    setTimeout(() => {
      window.scrollTo(0, storedScrollPosition);
    }, 100); // Delay to ensure content is rendered
  }, []);

  useEffect(() => {
    if (allData) {
      // Filter for MAB positions
      const MABPositions = allData?.filter(
        (position) => position.StrategyType === "Merger Arbitrage"
      );
      setMABData(MABPositions);

      // Filter for positions with downside prices
      const positionsWithDownsidePrices = MABPositions?.filter(
        (position) => position.Dealbreak_Downside_Price !== null
      );
      setPositions_with_downside_prices_data(positionsWithDownsidePrices);

      // Filter for positions with missing downside prices
      const positionsWithMissingDownsidePrices = MABPositions?.filter(
        (position) => position.Dealbreak_Downside_Price === null
      );
      setPositions_with_missing_downside_prices(
        positionsWithMissingDownsidePrices
      );
    }
  }, [allData]);

  // Add separate useEffect hooks for each table's filter
  useEffect(() => {
    if (globalFilterRisk !== savedGlobalFilterRisk) {
      setSavedGlobalFilterRisk(globalFilterRisk || "");
      if (!globalFilterRisk) {
        localStorage.removeItem("mabRiskTableGlobalFilter");
      } else {
        localStorage.setItem("mabRiskTableGlobalFilter", globalFilterRisk);
      }
    }
  }, [globalFilterRisk, savedGlobalFilterRisk]);

  useEffect(() => {
    if (globalFilterMissingPrices !== savedGlobalFilterMissingPrices) {
      setSavedGlobalFilterMissingPrices(globalFilterMissingPrices || "");
      if (!globalFilterMissingPrices) {
        localStorage.removeItem("mabRiskMissingPricesTableGlobalFilter");
      } else {
        localStorage.setItem(
          "mabRiskMissingPricesTableGlobalFilter",
          globalFilterMissingPrices
        );
      }
    }
  }, [globalFilterMissingPrices, savedGlobalFilterMissingPrices]);

  useEffect(() => {
    if (globalFilterArbMonitor !== savedGlobalFilterArbMonitor) {
      setSavedGlobalFilterArbMonitor(globalFilterArbMonitor || "");
      if (!globalFilterArbMonitor) {
        localStorage.removeItem("mabRiskArbMonitorTableGlobalFilter");
      } else {
        localStorage.setItem(
          "mabRiskArbMonitorTableGlobalFilter",
          globalFilterArbMonitor
        );
      }
    }
  }, [globalFilterArbMonitor, savedGlobalFilterArbMonitor]);

  // ===== HANDLERS =====
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("mabRiskActiveTab", tab);
  };

  // ===== MEMOIZED VALUES =====
  const risk_table = useMaterialReactTable({
    columns: getRiskTableColumns(
      theme,
      globalFilterRisk,
      formatCurrency,
      formatAmount
    ),
    data: MABData,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    onGlobalFilterChange: setGlobalFilterRisk,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "risk_to_downside", desc: false }],
      density: "compact",
      expanded: expandedRiskRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: savedGlobalFilterRisk,
    },
    state: {
      expanded: expandedRiskRows,
      globalFilter: globalFilterRisk,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedRiskRows) : updater;
      setExpandedRiskRows(newExpanded);
      localStorage.setItem(
        "mabPnLTableExpandedRows",
        JSON.stringify(newExpanded)
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 220px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        minWidth: "100%",
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const risk_table_missing_prices = useMaterialReactTable({
    columns: getRiskTableColumns(
      theme,
      globalFilterRisk,
      formatCurrency,
      formatAmount
    ),
    data: positions_with_missing_downside_prices,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    onGlobalFilterChange: setGlobalFilterMissingPrices,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "StrategyName", desc: false }],
      density: "compact",
      expanded: expandedRiskRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
      showGlobalFilter: true,
      showColumnFilters: false,
      globalFilter: savedGlobalFilterMissingPrices,
    },
    state: {
      expanded: expandedRiskRows,
      globalFilter: globalFilterMissingPrices,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedRiskRows) : updater;
      setExpandedRiskRows(newExpanded);
      localStorage.setItem(
        "mabPnLTableExpandedRows",
        JSON.stringify(newExpanded)
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 220px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        minWidth: "100%",
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // ===== RENDER LOGIC =====
  const renderTabContent = () => {
    switch (activeTab) {
      case "tab1":
        return (
          <Box sx={{ width: "100%" }}>
            <MABSummaryStats
              MABData={MABData}
              NAV_NUMBER={NAV_NUMBER}
              arb_monitor_data={arb_monitor_data}
              strategyAllowedRegionsData={
                datasets?.strategyAllowedRegionsData || []
              }
              strategyRegionsData={datasets?.strategyRegionsData || []}
            />
          </Box>
        );
      case "tab2":
        return (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <PageHeader
              sx={{
                px: "20px",
                justifyContent: "flex-start",
                alignItems: "end",
              }}
              title={"MAB Portfolio Risk"}
              action={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center", // vertically center items
                    justifyContent: "flex-start", // left align
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {/* Search Field */}
                  <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                    <MRT_GlobalFilterTextField
                      table={risk_table}
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: theme.shape.borderRadius,
                          border: `1px solid ${theme.palette.divider}`,
                          fontSize: "0.875rem",
                          "&:hover": {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.dark,
                          },
                          "&.Mui-focused": {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.dark,
                            boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                          },
                        },
                        "& .MuiInputBase-input": {
                          padding: theme.spacing(0.75, 1.25),
                          color: theme.palette.text.primary,
                          "&::placeholder": {
                            color: theme.palette.text.secondary,
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              }
            />

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f5f5f5",
                paddingLeft: "8px",
                height: "calc(100% - 40px)",
                overflow: "hidden",
              }}
            >
              <MaterialReactTable
                table={risk_table}
                muiTablePaperProps={{
                  sx: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  },
                }}
                muiTableContainerProps={{
                  sx: {
                    flex: "1 1 auto",
                    overflow: "auto",
                  },
                }}
              />
            </Box>
          </Box>
        );
      case "tab3":
        return (
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 100px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ArbMonitor arb_monitor_data={arb_monitor_data || []} />
          </Box>
        );
      case "tab4":
        return (
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 100px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <BondMonitor bond_monitor_data={bond_monitor_data || []} />
          </Box>
        );
      case "tab5":
        return (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <PageHeader
              sx={{
                px: "20px",
                justifyContent: "flex-start",
                alignItems: "end",
              }}
              title={"Missing Downside Prices"}
              action={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center", // vertically center items
                    justifyContent: "flex-start", // left align
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {/* Search Field */}
                  <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                    <MRT_GlobalFilterTextField
                      table={risk_table_missing_prices}
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: theme.shape.borderRadius,
                          border: `1px solid ${theme.palette.divider}`,
                          fontSize: "0.875rem",
                          "&:hover": {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.dark,
                          },
                          "&.Mui-focused": {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.dark,
                            boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                          },
                        },
                        "& .MuiInputBase-input": {
                          padding: theme.spacing(0.75, 1.25),
                          color: theme.palette.text.primary,
                          "&::placeholder": {
                            color: theme.palette.text.secondary,
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              }
            />

            <Box
              sx={{
                flex: 1,
                overflow: "auto", // Changed to auto to ensure footer visibility
                backgroundColor: theme.palette.background.paper,
                padding: theme.spacing(1),
              }}
            >
              <MaterialReactTable table={risk_table_missing_prices} />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  // ===== RETURN =====
  return (
    <Box sx={{ width: "100%" }}>
      {!dashboardOnly && (
      <CardWrapper sx={{ mb: 0 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabClick}
        />
      </CardWrapper>
      )}

      {/* Tab Content */}
      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        {renderTabContent()}
      </Box>
    </Box>
  );
};

// Update the WinnerLoserBox component
const WinnerLoserBox = ({ strategies = [], NAV_NUMBER }) => (
  <Box sx={{ width: "100%" }}>
    <Box
      sx={{
        height: "200px",
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f5f5f5",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#bdbdbd",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#9e9e9e",
        },
      }}
    >
      {strategies?.map((strategy, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "8px 12px",
            borderBottom:
              index < strategies?.length - 1 ? "1px solid #f0f0f0" : "none",
            "&:hover": {
              backgroundColor: "#f8f9fa",
            },
            transition: "background-color 0.2s",
          }}
        >
          {/* Strategy Name Column */}
          <Typography
            sx={{
              width: "50%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "bold", // Changed from 500 to bold
              fontSize: "0.875rem",
              color: "#2c3e50",
              textAlign: "left",
            }}
          >
            {strategy.name}
          </Typography>

          {/* Dollar PnL Column */}
          <Typography
            sx={{
              width: "25%",
              textAlign: "right",
              fontWeight: "bold", // Changed from 500 to bold
              fontSize: "0.875rem",
              color: strategy.pnl < 0 ? "#e53935" : "#43a047",
            }}
          >
            {strategy.pnl < 0
              ? `($${Math.abs(Math.round(strategy.pnl)).toLocaleString()})`
              : `$${Math.round(strategy.pnl).toLocaleString()}`}
          </Typography>

          {/* BPS Column */}
          <Typography
            sx={{
              width: "25%",
              textAlign: "right",
              fontWeight: "bold", // Changed from 500 to bold
              fontSize: "0.875rem",
              color: strategy.pnl < 0 ? "#e53935" : "#43a047",
              paddingRight: "4px",
            }}
          >
            {strategy.pnl < 0
              ? `${((strategy.pnl / NAV_NUMBER) * 10000).toFixed(1)}`
              : `+${((strategy.pnl / NAV_NUMBER) * 10000).toFixed(1)}`}
            {" bps"}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default MAB_Risk;
